import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icons';
import { TKLogo } from './Logo';
import { useBreakpoint } from '../lib/useBreakpoint';
import { useAuth } from '../lib/auth';
import { useMovers } from '../lib/dataHooks';

export type NavId = 'dashboard' | 'portfolio' | 'markets' | 'bots' | 'watchlist' | 'wallet';

interface TopBarProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onNav: (id: NavId) => void;
  onSignOut: () => void;
  currentNav: NavId | string;
  showTickerTape?: boolean;
}

export const TopBar = ({
  theme,
  onToggleTheme,
  onNav,
  onSignOut,
  currentNav,
  showTickerTape = true,
}: TopBarProps) => {
  const navItems: { id: NavId; label: string; icon: IconName }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home' },
    { id: 'portfolio', label: 'Portfolio', icon: 'briefcase' },
    { id: 'markets', label: 'Markets', icon: 'chart' },
    { id: 'bots', label: 'Bots', icon: 'sliders' },
    { id: 'watchlist', label: 'Watchlist', icon: 'star' },
    { id: 'wallet', label: 'Wallet', icon: 'wallet' },
  ];
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const [navOpen, setNavOpen] = useState(false);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'color-mix(in oklch, var(--tk-bg) 88%, transparent)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--tk-line)',
      }}
    >
      <div
        style={{
          maxWidth: 1440,
          margin: '0 auto',
          padding: isMobile ? '12px 16px' : '14px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 12 : isTablet ? 16 : 32,
        }}
      >
        {isMobile && (
          <button
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Open menu"
            style={{
              width: 38,
              height: 38,
              borderRadius: 'var(--tk-radius-sm)',
              background: 'var(--tk-surface-2)',
              border: '1px solid var(--tk-line)',
              color: 'var(--tk-ink-2)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        )}
        <TKLogo size={isMobile ? 24 : 28} />

        {!isMobile && (
          <nav style={{ display: 'flex', gap: 4, marginLeft: isTablet ? 8 : 16, flexWrap: 'nowrap' }}>
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => onNav(n.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: isTablet ? '8px 10px' : '8px 14px',
                  fontFamily: 'var(--tk-sans)',
                  fontSize: 13.5,
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: currentNav === n.id ? 'var(--tk-ink)' : 'var(--tk-ink-3)',
                  background: currentNav === n.id ? 'var(--tk-surface-2)' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--tk-radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.18s var(--tk-ease)',
                }}
                onMouseEnter={(e) => {
                  if (currentNav !== n.id) e.currentTarget.style.color = 'var(--tk-ink)';
                }}
                onMouseLeave={(e) => {
                  if (currentNav !== n.id) e.currentTarget.style.color = 'var(--tk-ink-3)';
                }}
              >
                <Icon name={n.icon} size={15} />
                {!isTablet && n.label}
              </button>
            ))}
          </nav>
        )}

        <div style={{ flex: 1 }} />

        {!isMobile && !isTablet && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--tk-surface-2)',
              border: '1px solid var(--tk-line)',
              borderRadius: 'var(--tk-radius-sm)',
              padding: '8px 12px',
              width: 280,
              minWidth: 0,
              flexShrink: 1,
              color: 'var(--tk-ink-3)',
              fontSize: 13,
            }}
          >
            <Icon name="search" size={15} />
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Search stocks, news…</span>
            <span
              style={{
                marginLeft: 'auto',
                fontFamily: 'var(--tk-mono)',
                fontSize: 11,
                padding: '2px 6px',
                background: 'var(--tk-bg)',
                borderRadius: 4,
                border: '1px solid var(--tk-line)',
              }}
            >
              ⌘K
            </span>
          </div>
        )}

        <button
          onClick={onToggleTheme}
          aria-label="Toggle theme"
          style={{
            width: 38,
            height: 38,
            borderRadius: 'var(--tk-radius-sm)',
            background: 'var(--tk-surface-2)',
            border: '1px solid var(--tk-line)',
            color: 'var(--tk-ink-2)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s var(--tk-ease)',
          }}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
        </button>

        <button
          aria-label="Notifications"
          style={{
            width: 38,
            height: 38,
            borderRadius: 'var(--tk-radius-sm)',
            background: 'var(--tk-surface-2)',
            border: '1px solid var(--tk-line)',
            color: 'var(--tk-ink-2)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Icon name="bell" size={16} />
          <span
            style={{
              position: 'absolute',
              top: 8,
              right: 9,
              width: 7,
              height: 7,
              borderRadius: 99,
              background: 'var(--tk-down)',
              border: '2px solid var(--tk-surface-2)',
            }}
          />
        </button>

        <UserMenu onSignOut={onSignOut} />
      </div>

      {isMobile && navOpen && (
        <nav
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '8px 16px 14px',
            borderTop: '1px solid var(--tk-line-2)',
            background: 'var(--tk-surface)',
          }}
        >
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                onNav(n.id);
                setNavOpen(false);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                fontFamily: 'var(--tk-sans)',
                fontSize: 14,
                fontWeight: 600,
                color: currentNav === n.id ? 'var(--tk-ink)' : 'var(--tk-ink-2)',
                background: currentNav === n.id ? 'var(--tk-surface-2)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--tk-radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Icon name={n.icon} size={16} />
              {n.label}
            </button>
          ))}
        </nav>
      )}

      {showTickerTape && (
        <div style={{ maxWidth: 1440, margin: '0 auto' }}>
          <TickerTape />
        </div>
      )}
    </header>
  );
};

interface UserMenuProps {
  onSignOut: () => void;
}

const computeInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserMenu = ({ onSignOut }: UserMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const displayName = user?.name?.trim() || 'Guest';
  const displayEmail = user?.email ?? '';
  const initials = computeInitials(displayName);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 12px 4px 4px',
          background: 'var(--tk-surface-2)',
          border: '1px solid var(--tk-line)',
          borderRadius: 999,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 99,
            background: 'linear-gradient(135deg, var(--tk-accent), oklch(0.55 0.13 195))',
            color: 'var(--tk-accent-ink)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--tk-sans)',
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {initials}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tk-ink)' }}>{displayName}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: 'var(--tk-ink-3)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.18s',
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 220,
            padding: 6,
            background: 'var(--tk-surface)',
            border: '1px solid var(--tk-line)',
            borderRadius: 'var(--tk-radius-md)',
            boxShadow: 'var(--tk-shadow-lg)',
            zIndex: 50,
          }}
        >
          <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid var(--tk-line-2)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tk-ink)' }}>
              {displayName}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--tk-ink-3)',
                fontFamily: 'var(--tk-mono)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 200,
              }}
              title={displayEmail}
            >
              {displayEmail || '—'}
            </div>
          </div>
          <UserMenuItem icon="settings" label="Account settings" />
          <UserMenuItem icon="shield" label="Security & 2FA" />
          <UserMenuItem icon="receipt" label="Tax statements" />
          <div style={{ height: 1, background: 'var(--tk-line-2)', margin: '4px 0' }} />
          <button
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '9px 12px',
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tk-down)',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tk-down-soft)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const UserMenuItem = ({ label, icon }: { label: string; icon: 'settings' | 'shield' | 'receipt' }) => (
  <button
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      width: '100%',
      padding: '9px 12px',
      borderRadius: 6,
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 500,
      color: 'var(--tk-ink-2)',
      textAlign: 'left',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'var(--tk-surface-2)';
      e.currentTarget.style.color = 'var(--tk-ink)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = 'var(--tk-ink-2)';
    }}
  >
    <span style={{ width: 15, height: 15, display: 'inline-flex', color: 'var(--tk-ink-3)' }}>
      {icon === 'settings' && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      )}
      {icon === 'shield' && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      )}
      {icon === 'receipt' && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 2V2H4z" />
          <line x1="8" y1="8" x2="16" y2="8" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      )}
    </span>
    {label}
  </button>
);

const TickerTape = () => {
  // The tape shows the worker's hot set — the user's holdings, watchlists and
  // the baseline names — ordered by day change. Real data only: with nothing
  // to show the tape is simply empty.
  const moversQ = useMovers(50, 30_000, 'active');
  const live = moversQ.data?.gainers ?? [];
  const liveItems = live.map((m) => ({ sym: m.sym, price: m.last, change: m.changePct }));
  if (liveItems.length === 0) return null;
  // Duplicate the list so the marquee animation has continuous content.
  const items = liveItems.concat(liveItems);
  return (
    <div
      style={{
        borderTop: '1px solid var(--tk-line-2)',
        background: 'var(--tk-bg)',
        overflow: 'hidden',
        maskImage: 'linear-gradient(90deg, transparent, black 4%, black 96%, transparent)',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          animation: 'tk-marquee 80s linear infinite',
        }}
      >
        {items.map((s, i) => (
          <div
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              fontSize: 12.5,
            }}
          >
            <span style={{ fontWeight: 700, letterSpacing: '0.02em' }}>{s.sym}</span>
            <span className="mono" style={{ color: 'var(--tk-ink-2)' }}>
              {s.price.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span
              className="mono"
              style={{
                color: s.change >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
                fontWeight: 600,
              }}
            >
              {s.change >= 0 ? '+' : ''}
              {s.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
